import { fromJS } from 'immutable';
import memoizee from 'memoizee';
import { createSelector } from 'reselect';
import {
  captionsSizeSelector,
  captionsStyleSelector,
  captionsVideoSourceSelector,
  createLayoutSegmentByIdSelector,
  embedManualTranscriptSelector,
} from 'redux/modules/embed/selectors';
import { convertTranscriptToCues } from 'utils/caption-adv-animation';
import { filterTranscriptByTime } from 'utils/transcript';
import { SceneCaptionsOverride } from './state/types';
import { getAutoCaptionsPosition } from './utils';

const captionsPlayOffstMillisSelector = createSelector(
  [captionsVideoSourceSelector],
  video => video?.get('playFromMillis', 0) ?? 0,
);

export const createLayoutSegmentCaptionCuesSelector = memoizee(
  (videoId: string, segmentId: string) =>
    createSelector(
      [
        embedManualTranscriptSelector,
        createLayoutSegmentByIdSelector(videoId, segmentId),
        captionsPlayOffstMillisSelector,
      ],
      (transcript, layoutSegment, playOffsetMillis) => {
        const chunks = transcript?.get('transcript')?.toJS();

        if (!chunks || !layoutSegment) {
          return [];
        }

        const segmentStartMillis = layoutSegment.playOffsetMillis;
        const segmentEndMillis =
          segmentStartMillis + layoutSegment.durationMillis;

        const clipStartMillis = segmentStartMillis - playOffsetMillis;
        const clipEndMillis = segmentEndMillis - playOffsetMillis;

        const filteredChunks = filterTranscriptByTime(
          chunks,
          clipStartMillis,
          clipEndMillis,
        );

        return convertTranscriptToCues(filteredChunks, 0);
      },
    ),
);

export const layoutSegmentCaptionsPositionSelector = createSelector(
  [
    captionsSizeSelector,
    (_, slotCount: number | undefined) => slotCount,
    (
      _,
      _slotCount: number | undefined,
      captionsOverride: SceneCaptionsOverride | undefined,
    ) => captionsOverride,
  ],
  (captionsSize, slotCount, captionsOverrideState) => {
    // Priority 1: if there's an override, use it
    if (captionsOverrideState?.position) {
      return captionsOverrideState.position;
    }

    // Priotity 2: calculate auto-position override if applicable
    if (!captionsSize || slotCount === undefined) {
      return null;
    }

    return getAutoCaptionsPosition(slotCount, captionsSize);
  },
);

export const layoutSegmentCaptionsStyleSelector = createSelector(
  [captionsStyleSelector, layoutSegmentCaptionsPositionSelector],
  (baseCaptionsStyle, positionOverride) => {
    // if no base style, return undefined
    if (!baseCaptionsStyle) {
      return undefined;
    }

    // if no override, return base style unchanged
    if (!positionOverride) {
      return baseCaptionsStyle;
    }

    return baseCaptionsStyle.set('position', fromJS(positionOverride));
  },
);
